import { useRef, useState, useEffect, useCallback } from 'react';

import eventBus, { EventTypes } from 'src/utils/eventBus';

import { WorkflowDashboardRunDto } from 'src/@types/workflow/types';
import { getWorkflowRunsForOwnDocuments } from 'src/services/workflow/workflowDashboardService';

export default function useDashboardWorkflowRuns(limit: number) {
  const [runs, setRuns] = useState<WorkflowDashboardRunDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);

  const currentLoad = useRef<AbortController | null>(null);

  const reload = useCallback(async () => {
    currentLoad.current?.abort();

    const load = new AbortController();
    currentLoad.current = load;

    setIsLoading(true);
    setHasLoadFailed(false);

    try {
      const latestRuns = await getWorkflowRunsForOwnDocuments(limit, load.signal);

      if (!load.signal.aborted) {
        setRuns(latestRuns);
      }
    } catch {
      if (load.signal.aborted) {
        return;
      }

      setHasLoadFailed(true);
    } finally {
      if (!load.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [limit]);

  useEffect(() => {
    reload();

    return () => currentLoad.current?.abort();
  }, [reload]);

  useEffect(() => {
    const subscriptions = [
      eventBus.subscribe(EventTypes.DocumentSigned, reload),
      eventBus.subscribe(EventTypes.DocumentApproved, reload),
    ];

    return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, [reload]);

  return { runs, isLoading, hasLoadFailed };
}
